import Tab from "@mui/material/Tab";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import Select from "@mui/material/Select";
import Modal from "@mui/material/Modal";
import Skeleton from "@mui/material/Skeleton";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import React, { useEffect, useState } from "react";
import ListItemText from "@mui/material/ListItemText";
import { useSelector, useDispatch } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { hasValidAdvisor } from "../BusinessCards.js";
import { getRequest, postRequest, showMessage } from "../../../../config.js";
import { setCompanies } from "../../../../reducers/companiesReducer.js";
import { toggleModal } from "../../../../reducers/modalReducer.js";
import TempLeadMatricReport from "../../../../components/revenueLeadAllocation/TempLeadMatricReport.js";

// ─── Sub-tab wrapper (mirrors RevenueLeadsAllocation.js structure) ────────────
export default function TempRevenueLeadsAllocation({ profileClass, advisors, setRefresh }) {
    console.log('advisors', advisors);
    const [step, setStep] = useState("modules");
    return (
        <TabContext value={step}>
            <TabList
                onChange={(_, value) => setStep(value)}
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    "& .MuiTabs-flexContainer": { justifyContent: "center" },
                }}
            >
                <Tab className="fs1rem fw600 mt1rem mb1rem" label="Modules" value="modules" />
                <Tab className="fs1rem fw600 mt1rem mb1rem" label="Lead Metrics Report" value="reports" />
            </TabList>
            <TabPanel className="w100" value="modules">
                <TempRevenueLeadsTable profileClass={profileClass} advisors={advisors} setRefresh={setRefresh} />
            </TabPanel>
            <TabPanel className="w100" value="reports">
                <TempLeadMatricReport />
            </TabPanel>
        </TabContext>
    );
}

// ─── Allocation table (all logic lives here) ──────────────────────────────────
function TempRevenueLeadsTable({ profileClass, advisors, setRefresh }) {
    const user = useSelector(state => state.user);
    const companies = useSelector(state => state.companies);
    const modal = useSelector(state => state.modal);
    const dispatch = useDispatch();

    const [allCompanies, setAllCompanies] = useState([]);
    const [tempModules, setTempModules] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    // Flat list of all TempSections, each enriched with .allocation map
    const [sections, setSections] = useState([]);
    const [openAccordions, setOpenAccordions] = useState({});
    const [resendAdvisor, setResendAdvisor] = useState({});

    // ─── Flatten companies (parent + children) ────────────────────────────────────
    useEffect(() => {
        if (!companies?.length) return;
        const seen = new Set();
        const flat = [];
        for (const comp of companies) {
            const copy = { ...comp };
            delete copy.childrenCompanies;
            if (!seen.has(comp._id)) { flat.push(copy); seen.add(comp._id); }
            if (Array.isArray(comp.childrenCompanies)) {
                for (const child of comp.childrenCompanies) {
                    const childCopy = { ...child };
                    delete childCopy.childrenCompanies;
                    if (!seen.has(child._id)) { flat.push(childCopy); seen.add(child._id); }
                }
            }
        }
        setAllCompanies(flat);
    }, [companies]);

    // ─── Fetch all TempModules + Checklists with their sections ──────────────────
    useEffect(() => {
        getRequest('/modules/all-modules-section', 'not-showMessage').then(res => {
            if (res?.code === 200) {
                const body = res.body;
                const combined = Array.isArray(body)
                    ? body
                    : [
                        ...(body?.modules || []).filter(m => m.sections?.length > 0),
                        ...(body?.checklists || []).filter(m => m.sections?.length > 0).map(m => ({ ...m, isChecklist: true })),
                    ];
                setTempModules(combined);
            }
        }).finally(() => setIsLoading(false));
    }, []);

    // ─── Build sections with allocation data from company.revenuelead ─────────────
    // Each section gets: { sectionNo, sectionName, moduleId, moduleName, moduleKey,
    //   allocation: { [companyId]: { [advisorId]: { ...advisor, allocationModule: { [sectionNo]: bool } } } } }
    useEffect(() => {
        if (!tempModules.length || !allCompanies.length) return;

        const flatSections = tempModules.flatMap(m =>
            m.sections.map(s => ({
                ...s,
                moduleId: m._id,
                moduleName: m.moduleName,
                moduleKey: m.moduleKey,
            }))
        );

        const sectionObj = JSON.parse(JSON.stringify(flatSections));

        for (const comp of allCompanies) {
            if (!comp.revenuelead) continue;
            for (const advisorId of Object.keys(comp.revenuelead)) {
                const advisorSettings = comp.revenuelead[advisorId];
                const advisor = advisors.find(({ _id }) => _id == advisorId);
                if (!advisorSettings?.modules || !advisor) continue;

                for (let i = 0; i < sectionObj.length; i++) {
                    if (!sectionObj[i].allocation) sectionObj[i].allocation = {};
                    if (!sectionObj[i].allocation[comp._id]) sectionObj[i].allocation[comp._id] = {};
                    sectionObj[i].allocation[comp._id][advisorId] = {
                        ...advisor,
                        allocationModule: advisorSettings.modules,
                    };
                }
            }
        }
        setSections(sectionObj);
    }, [allCompanies, tempModules]);

    const toggleAccordion = (moduleId) => {
        setOpenAccordions(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
    };

    // Returns true if an advisor has all temp sections allocated for a given company
    const checkAdvisorHasAllAllocations = ({ advisor, company: comp }) => {
        if (advisor && comp?.revenuelead?.[advisor._id]?.modules) {
            const allTempSectionNos = sections.map(s => s.sectionNo);
            if (!allTempSectionNos.length) return false;
            const allocatedSections = Object.entries(comp.revenuelead[advisor._id].modules)
                .filter(([, val]) => val === true)
                .map(([key]) => key);
            return allTempSectionNos.every(no => allocatedSections.includes(no));
        }
        return false;
    };

    // Returns true if an advisor has the temp section permission (stored in company.advisors)
    const hasAdvisorPermission = (advisor, comp, sectionNo) => {
        const userType = user.role === 'Advisor' ? 'advisors' : 'investors';
        return !!comp?.[userType]?.[advisor._id]?.modules?.[sectionNo];
    };

    // ─── Single-section allocation toggle ─────────────────────────────────────────
    const handleLeadAllocation = async (e, advisor, sectionNo, comp) => {
        if (!advisor.emailVerified) {
            setResendAdvisor(advisor);
            dispatch(toggleModal({ open: 'resendInviteMail' }));
            return;
        }

        const status = e.target.checked;
        const res = await postRequest(`/user/allocateRevenueLead/${comp._id}`, {
            advisor,
            company: comp,
            advisorId: advisor._id,
            sectionNo,
            status,
            firmId: user.companyId._id,
        });

        if (res?.code === 200) {
            const sectionObj = JSON.parse(JSON.stringify(sections));
            const sectionIndex = sectionObj.findIndex(s => s.sectionNo === sectionNo);

            if (sectionIndex >= 0 && sectionObj[sectionIndex].allocation?.[comp._id]) {
                // Backend sets all other advisors to false for this section when one is set true
                Object.keys(sectionObj[sectionIndex].allocation[comp._id]).forEach(advId => {
                    const advData = sectionObj[sectionIndex].allocation[comp._id][advId];
                    sectionObj[sectionIndex].allocation[comp._id][advId] = {
                        ...advData,
                        allocationModule: {
                            ...(advData.allocationModule || {}),
                            [sectionNo]: advId === advisor._id ? status : false,
                        },
                    };
                });
            }
            setSections(sectionObj);

            // Keep allCompanies in Redux in sync
            const updatedCompanies = allCompanies.map(item => {
                if (item._id !== comp._id) return item;
                const newRevenuelead = { ...(item.revenuelead || {}) };
                newRevenuelead[advisor._id] = {
                    ...newRevenuelead[advisor._id],
                    modules: {
                        ...(newRevenuelead[advisor._id]?.modules || {}),
                        [sectionNo]: status,
                    },
                };
                if (status) {
                    for (const id in newRevenuelead) {
                        if (id !== advisor._id && newRevenuelead[id]?.modules) {
                            newRevenuelead[id] = {
                                ...newRevenuelead[id],
                                modules: { ...newRevenuelead[id].modules, [sectionNo]: false },
                            };
                        }
                    }
                }
                return { ...item, revenuelead: newRevenuelead };
            });
            dispatch(setCompanies(updatedCompanies));

            showMessage(res.message);
            setRefresh(r => !r);
        }
    };

    // ─── Bulk-allocate all temp sections for an advisor on a company ──────────────
    const handleMultiLeadAllocation = async (e, advisor, sectionNos, comp) => {
        if (!advisor.emailVerified) {
            setResendAdvisor(advisor);
            dispatch(toggleModal({ open: 'resendInviteMail' }));
            return;
        }

        const _checked = e.target.checked;

        const result = await postRequest(`/user/allocateMultiRevenueLead/${comp._id}`, {
            advisor,
            company: comp,
            advisorId: advisor._id,
            sectionNo: sectionNos,
            status: _checked,
            firmId: user.companyId._id,
        });

        if (result?.code === 200) {
            const sectionObj = JSON.parse(JSON.stringify(sections));
            sectionObj.forEach((section, sectionIndex) => {
                if (!sectionObj[sectionIndex].allocation) sectionObj[sectionIndex].allocation = {};
                if (!sectionObj[sectionIndex].allocation[comp._id]) {
                    sectionObj[sectionIndex].allocation[comp._id] = {};
                }

                if (_checked) {
                    // Ensure the advisor entry exists
                    if (!sectionObj[sectionIndex].allocation[comp._id][advisor._id]) {
                        sectionObj[sectionIndex].allocation[comp._id][advisor._id] = { ...advisor, allocationModule: {} };
                    }
                    sectionObj[sectionIndex].allocation[comp._id][advisor._id] = {
                        ...sectionObj[sectionIndex].allocation[comp._id][advisor._id],
                        allocationModule: {
                            ...(sectionObj[sectionIndex].allocation[comp._id][advisor._id].allocationModule || {}),
                            [section.sectionNo]: true,
                        },
                    };
                    // Backend sets all other advisors to false for this section
                    Object.keys(sectionObj[sectionIndex].allocation[comp._id]).forEach(advId => {
                        if (advId !== advisor._id) {
                            const advData = sectionObj[sectionIndex].allocation[comp._id][advId];
                            sectionObj[sectionIndex].allocation[comp._id][advId] = {
                                ...advData,
                                allocationModule: {
                                    ...(advData.allocationModule || {}),
                                    [section.sectionNo]: false,
                                },
                            };
                        }
                    });
                } else {
                    if (sectionObj[sectionIndex].allocation[comp._id][advisor._id]) {
                        sectionObj[sectionIndex].allocation[comp._id][advisor._id] = {
                            ...sectionObj[sectionIndex].allocation[comp._id][advisor._id],
                            allocationModule: {
                                ...(sectionObj[sectionIndex].allocation[comp._id][advisor._id].allocationModule || {}),
                                [section.sectionNo]: false,
                            },
                        };
                    }
                }
            });
            setSections(sectionObj);

            // Keep allCompanies in Redux in sync
            const currentModules = allCompanies
                .find(c => c._id === comp._id)?.revenuelead?.[advisor._id]?.modules || {};
            const newModules = { ...currentModules };
            sectionNos.forEach(sno => { newModules[sno] = _checked; });

            const updatedCompanies = allCompanies.map(item => {
                if (item._id !== comp._id) return item;
                const newRevenuelead = { ...(item.revenuelead || {}) };
                newRevenuelead[advisor._id] = {
                    ...newRevenuelead[advisor._id],
                    modules: newModules,
                };
                if (_checked) {
                    for (const id in newRevenuelead) {
                        if (id !== advisor._id && newRevenuelead[id]?.modules) {
                            const updatedMods = { ...newRevenuelead[id].modules };
                            sectionNos.forEach(sno => { updatedMods[sno] = false; });
                            newRevenuelead[id] = { ...newRevenuelead[id], modules: updatedMods };
                        }
                    }
                }
                return { ...item, revenuelead: newRevenuelead };
            });
            dispatch(setCompanies(updatedCompanies));
        }

        showMessage(result.message);
        setRefresh(r => !r);
    };

    const onResendLinkModalClose = () => {
        setResendAdvisor({});
        dispatch(toggleModal({ open: '' }));
    };

    const submitResendInvite = async () => {
        const res = await getRequest(`/user/resendAdvisorInvite/${resendAdvisor._id}`);
        if (res?.code === 200) {
            showMessage(res.message);
            onResendLinkModalClose();
        }
    };

    // ─── Empty / loading states ───────────────────────────────────────────────────
    if (!allCompanies.length) {
        return (
            <div className="df jcc c15 fs1-75rem fw500 lh2 mt2rem">
                No clients added for allocation
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="sectionWrapper p1rem fs0-75rem pr mr2rem ml1rem pr">
                <div className="table-permission">
                    <table id="table-temp-revenue-allocation" className="table-userPermissions table-clientPermissions">
                        <thead className="ps t0">
                            <tr>
                                <th className="index-table"></th>
                                {allCompanies.map((_, cIndex) => (
                                    <th className="user-area" key={`skel-rev-comp-${cIndex}`}>
                                        <div className="df aic">
                                            <div className="mr1rem wfull">
                                                <Skeleton variant="text" width={110} height={22} />
                                                <Skeleton variant="text" width={58} height={16} />
                                            </div>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3].map(mIdx => (
                                <React.Fragment key={`skel-rev-m-${mIdx}`}>
                                    <tr style={{ background: '#f8f9fa' }}>
                                        <td style={{ padding: '10px 12px', paddingTop: mIdx > 1 ? '20px' : '10px', borderTop: mIdx > 1 ? '1px solid #DBDFEE' : 'none' }}>
                                            <div className="df aic" style={{ gap: '8px' }}>
                                                <Skeleton variant="rectangular" width={130 + mIdx * 25} height={18} sx={{ borderRadius: '4px' }} />
                                                <Skeleton variant="rectangular" width={48} height={18} sx={{ borderRadius: '4px' }} />
                                                <Skeleton variant="rectangular" width={72} height={16} sx={{ borderRadius: '4px' }} />
                                            </div>
                                        </td>
                                        {allCompanies.map((_, i) => (
                                            <td key={i} style={{ background: '#f8f9fa', borderTop: mIdx > 1 ? '1px solid #DBDFEE' : 'none' }} />
                                        ))}
                                    </tr>
                                    {mIdx === 1 && [1, 2, 3].map(sIdx => (
                                        <tr key={`skel-rev-s-${sIdx}`}>
                                            <td style={{ background: '#fafbfe', padding: '8px 12px' }}>
                                                <div className="df aic" style={{ gap: '6px' }}>
                                                    <Skeleton variant="rectangular" width={58} height={14} sx={{ borderRadius: '3px' }} />
                                                    <Skeleton variant="rectangular" width={90 + sIdx * 25} height={14} sx={{ borderRadius: '3px' }} />
                                                </div>
                                            </td>
                                            {allCompanies.map((_, cIdx) => (
                                                <td key={`skel-rev-sc-${cIdx}`} className="cp">
                                                    <div className="w100 df jcsb aic bg1 p0-5rem br30px" style={{ border: '1px solid #DBDFEE' }}>
                                                        <Skeleton variant="circular" width={32} height={32} />
                                                        <Skeleton variant="circular" width={20} height={20} />
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    if (!tempModules.length) {
        return (
            <div className="df jcc c15 fs1-25rem fw500 lh2 mt2rem">
                No modules available
            </div>
        );
    }

    // Group sections back into per-module buckets for accordion rendering
    const sectionsByModule = tempModules
        .map(module => ({
            module,
            moduleSections: sections.filter(s => String(s.moduleId) === String(module._id)),
        }))
        .filter(({ moduleSections }) => moduleSections.length > 0);

    const moduleItems = sectionsByModule.filter(({ module }) => !module.isChecklist);
    const checklistItems = sectionsByModule.filter(({ module }) => module.isChecklist);

    const allTempSectionNos = sections.map(s => s.sectionNo);

    const renderModuleGroup = (items) => items.map(({ module, moduleSections }, mIdx) => (
        <React.Fragment key={module._id}>
            <tr onClick={() => toggleAccordion(module._id)} style={{ background: '#f8f9fa', cursor: 'pointer' }}>
                <td style={{ padding: '10px 12px', paddingTop: mIdx > 0 ? '20px' : '10px', borderTop: mIdx > 0 ? '1px solid #DBDFEE' : 'none' }}>
                    <div className="df aic jcsb w100">
                        <div className="df aic" style={{ gap: '8px' }}>
                            <span className="fw600 c15 fs1rem">{module.moduleName}</span>
                            {module.moduleKey && (
                                <span className="fs0-75rem c15 px0-5rem py0-25rem br5px" style={{ background: '#e9ecef', opacity: 0.8 }}>
                                    {module.moduleKey}
                                </span>
                            )}
                            <span className="fs0-75rem" style={{ color: '#888' }}>
                                ({moduleSections.length} section{moduleSections.length !== 1 ? 's' : ''})
                            </span>
                        </div>
                    </div>
                </td>
                {allCompanies.map((_, i) => (
                    <td key={i} style={{ background: '#f8f9fa', borderTop: mIdx > 0 ? '1px solid #DBDFEE' : 'none' }} />
                ))}
            </tr>
            {openAccordions[module._id] && moduleSections.map((section, sIdx) => (
                <tr key={section.sectionNo}>
                    <td className="c15 bg0" style={{ background: '#fafbfe' }}>
                        <span className="fw600 bg0" style={{ marginRight: '4px' }}>{section.sectionNo}</span>
                        {section.sectionName}
                    </td>
                    {allCompanies.map((comp, cIdx) => {
                        const isIRSection = section.moduleName === 'Investment Readiness';
                        const sectionModule = isIRSection ? tempModules.find(m => String(m._id) === String(section.moduleId)) : null;
                        const companyInIRModule = sectionModule?.companies ? String(comp._id) in sectionModule.companies : false;
                        const isDisabled = isIRSection && !companyInIRModule;
                        return (
                            <td className="c15 cp" key={`${cIdx}-rev-${section.sectionNo}-${sIdx}`}>
                                <div className="w100 df jcsb bg1 p0-5rem br30px" style={{ border: '1px solid #DBDFEE' }}>
                                    <div className="df">
                                        {!isDisabled && advisors
                                            .filter(advisor => comp?.revenuelead?.[advisor._id]?.modules?.[section.sectionNo] === true)
                                            .map((advisor, keyIndex) => {
                                                const tooltip = advisor.firstName ? `${advisor.firstName} ${advisor.lastName}` : 'Register to show your name here';
                                                return advisor?.profile ? (
                                                    <img key={`${keyIndex}-rev-av`} src={advisor.profile} style={{ border: '4px solid #09b255', cursor: 'pointer' }} className={`w2rem h2rem df jcc aic br100 mr0-5rem pr ${profileClass}`} data-tooltip={tooltip} alt="" onClick={() => handleLeadAllocation({ target: { checked: false } }, advisor, section.sectionNo, comp)} />
                                                ) : (
                                                    <span key={`${keyIndex}-rev-av-init`} style={{ border: '4px solid #09b255', cursor: 'pointer' }} data-tooltip={tooltip} className={`w2rem h2rem df jcc aic br100 mr0-5rem pr ${profileClass}`} onClick={() => handleLeadAllocation({ target: { checked: false } }, advisor, section.sectionNo, comp)}>
                                                        {advisor.firstName ? `${advisor.firstName[0]}${advisor.lastName[0]}` : '?'}
                                                    </span>
                                                );
                                            })
                                        }
                                    </div>
                                    <div className="pr">
                                        <Select multiple disabled={isDisabled} className="select-advisor w10" id={`rev-section-select-${comp._id}-${section.sectionNo}`} sx={{ "& .MuiSvgIcon-root": { color: "#4A67FF" } }} IconComponent={ExpandMoreIcon} value={[]} MenuProps={{ PaperProps: { style: { maxHeight: 300, width: 'auto', minWidth: '200px' } }, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, getContentAnchorEl: null, keepMounted: true, marginThreshold: 10, style: { WebkitTransform: 'none', position: 'fixed' } }}>
                                            {advisors.map((advisor, adIdx) => {
                                                const isValid = hasValidAdvisor(advisor);
                                                const isAllocated = comp?.revenuelead?.[advisor._id]?.modules?.[section.sectionNo] === true;
                                                return (
                                                    <MenuItem key={`rev-${section.sectionNo}-${comp._id}-${adIdx}`} value={advisor._id} className="select-advisor-list ttc" disabled={!isValid}>
                                                        <Checkbox checked={isAllocated} disabled={!isValid} onChange={() => handleLeadAllocation({ target: { checked: !isAllocated } }, advisor, section.sectionNo, comp)} />
                                                        <ListItemText primary={`${advisor.firstName} ${advisor.lastName} - ${advisor.title || advisor.designation || advisor.job || ''}`} />
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </div>
                                </div>
                            </td>
                        );
                    })}
                </tr>
            ))}
        </React.Fragment>
    ));

    return (
        <div className="sectionWrapper p1rem fs0-75rem pr mr2rem ml1rem pr">
            <div className="table-permission">
                <table id="table-temp-revenue-allocation" className="table-userPermissions table-clientPermissions">
                    <thead className="ps t0">
                        <tr>
                            <th className="index-table"></th>
                            {allCompanies.map((comp, cIndex) => (
                                <th className="user-area" key={`temp-rev-comp-${cIndex}`}>
                                    <div className="df aic">
                                        <div className="mr1rem wfull pr">
                                            <span className="di fs1rem c0">{comp.cName}</span>
                                            {comp.subscription ? (
                                                <span className="c7 di ml0-5rem">({comp.subscription?.toUpperCase()})</span>
                                            ) : (
                                                <span className="c8 di ml0-5rem">(INACTIVE)</span>
                                            )}
                                            <label className="">
                                                <Select multiple className="select-advisor w10 pa" id={`bulk-rev-select-${cIndex}`} sx={{ "& .MuiSvgIcon-root": { color: "#4A67FF" } }} IconComponent={ExpandMoreIcon} value={[]} MenuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } }}>
                                                    {advisors.map((advisor, idx) => (
                                                        <MenuItem key={`${cIndex}-bulk-rev-${idx}`} value={advisor} className="select-advisor-list ttc" disabled={!hasValidAdvisor(advisor)}>
                                                            <div className={!hasValidAdvisor(advisor) ? 'disable-advisor-checkbox' : ''}>
                                                                <Checkbox disabled={!hasValidAdvisor(advisor)} checked={checkAdvisorHasAllAllocations({ advisor, company: comp })} onClick={(e) => handleMultiLeadAllocation(e, advisor, allTempSectionNos, comp)} />
                                                            </div>
                                                            <ListItemText primary={`${advisor.firstName} ${advisor.lastName} - ${advisor.title || advisor.designation || advisor.job || ''}`} />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </label>
                                        </div>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {moduleItems.length > 0 && (
                            <>
                                <tr>
                                    <td colSpan={allCompanies.length + 1} style={{ padding: '16px 0 8px', borderBottom: '2px solid #4A67FF' }}>
                                        <span className="fw600 c3 fs1-25rem">Modules</span>
                                    </td>
                                </tr>
                                {renderModuleGroup(moduleItems)}
                            </>
                        )}
                        {checklistItems.length > 0 && (
                            <>
                                <tr>
                                    <td colSpan={allCompanies.length + 1} style={{ padding: '16px 0 8px', borderBottom: '2px solid #4A67FF', paddingTop: moduleItems.length > 0 ? '32px' : '16px' }}>
                                        <span className="fw600 c3 fs1-25rem">Checklists</span>
                                    </td>
                                </tr>
                                {renderModuleGroup(checklistItems)}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
            <Modal open={modal?.open === 'resendInviteMail'} onClose={onResendLinkModalClose} className="backdrop td500 zi4 pf t0 l0 w100 h110vh df jcc aic">
                <div className="mah90 oya bg1 shadow w100 px2rem py3rem bsbb df fdc m1rem maw600px br5px pr">
                    <img className="h2rem pa t0 center-horizontally" src={require(`../../../../../images/logo.svg`)} alt="TODO" style={{ top: '15px' }} />
                    <button className="close-popup" onClick={onResendLinkModalClose}>
                        <svg style={{ position: 'relative', top: '4px' }} className="pen fds h1rem f1 mr0-5rem" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                            <path className="pen fa-primary" d="M310.6 361.4c12.5 12.5 12.5 32.75 0 45.25C304.4 412.9 296.2 416 288 416s-16.38-3.125-22.62-9.375L160 301.3L54.63 406.6C48.38 412.9 40.19 416 32 416S15.63 412.9 9.375 406.6c-12.5-12.5-12.5-32.75 0-45.25l105.4-105.4L9.375 150.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L160 210.8l105.4-105.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-105.4 105.4L310.6 361.4z" />
                        </svg>
                    </button>
                    <h1 className="text-gradient fs2rem fw600 tac ttu pt1rem">Resend Invite</h1>
                    <div className="df fdc gg1rem px2rem">
                        <p className="fs1rem py1rem tac">
                            Advisor <span className="fw600 ttc">{resendAdvisor.firstName} {resendAdvisor.lastName}</span> is currently
                            <span className="fw600 ttc"> Inactive</span>. Please send <span className="fw600 ttc">{resendAdvisor.firstName} {resendAdvisor.lastName}</span> an
                            invitation link to activate their account, enabling you to allocate them to any department
                        </p>
                        <div className="df jcsa">
                            <span onClick={onResendLinkModalClose} className="w30 shadow bg4 bg4-hover c1 td250 br5px px1rem py0-5rem fw600 cp ttu bsbb tac df jcc aic">Cancel</span>
                            <span onClick={submitResendInvite} className="w30 shadow bg3 bg4-hover c1 td250 br5px px1rem py0-5rem fw600 cp ttu bsbb tac df jcc aic">Send</span>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
